/**
 * Default/generic account statement -- pure data/HTML builders.
 *
 * Extracted from src/shared_assets/index.js's Account Statement subsystem (#5447
 * componentization pass). This is the "structured PDF statement" path used by
 * any client that isn't isBW/isMAR/a fund-method client -- currently Sheetz
 * (explicit isSheetz branch) and llproducts (the anonymous default branch),
 * plus any other client with no default_value_method override and no BW/MAR
 * flag (stakeholderfarms/onward/huber/onwardre/vermeer/onwardfe/portblakely as
 * of this writing). Called from renderAccountStatementPdf() (which stays in
 * index.js since it owns the loading-modal/PDF-download DOM wiring).
 *
 * Every behavior gated on `clientData.flags.statement_denomination_aware`
 * (#5447) is opt-in via that config flag, not tied to client identity -- it
 * defaults to false, so every client on this path keeps byte-identical
 * output until they explicitly turn it on (llproducts does, today).
 *
 * clientData/client/capTableData/window.lastCalculationResult/
 * window.lastTimeSeriesResult are read as bare/window globals here, matching
 * the window-bridge pattern the rest of this codebase uses (see CLAUDE.md's
 * AppState bridge) -- not passed as parameters, matching every other
 * already-extracted helper in this directory.
 */
import moment from 'moment';
import { getEffectiveDenomination, resolveOwnedLabel } from './denomination-resolver.js';
import { resolveClassTotalShares } from './class-aggregates.js';
import { getCertNumber } from './string-utils.js';
import { escapeStatementHtml, buildStatementChartSvg } from './account-statement-shared.js';

export function buildStatementData(targetUserData, transactions = [], transactionMode = 'all_transactions') {
	/* Builds statement data from all share classes */
	const classes = Object.keys(clientData.equity).filter((c) => !c.endsWith('-Debt'));
	const statementDate = moment().format('MMMM D, YYYY');
	const userShares = targetUserData.clients?.[client]?.shares || {};

	// Get calculator breakdown for share prices
	const calculatorBreakdown = window.lastCalculationResult?.breakdown || [];

	// Build data for each class
	const byClass = {};
	let totalValue = 0;

	// #5447: every field/behavior below this line that depends on `denominationAware`
	// is opt-in via clientData.flags.statement_denomination_aware (default false) --
	// every other client on this shared default statement path (Sheetz, plus any
	// client with no default_value_method override: stakeholderfarms/onward/huber/
	// onwardre/vermeer/onwardfe/portblakely as of this writing) gets byte-identical
	// behavior until they turn the flag on themselves.
	const denominationAware = clientData.flags?.statement_denomination_aware === true;
	classes.forEach((c) => {
		const calcClassData = calculatorBreakdown.find((cd) => cd.classKey === c);
		const equityConfig = clientData.equity[c] || {};
		const denomination = denominationAware ? getEffectiveDenomination(clientData, c) : 'shares';
		const sharesOwned = Number(userShares[c]?.owned || userShares[c]?.approved || 0);
		const currentPrice = calcClassData?.sharePrice || 0;
		// #5447: use the calculator's own denomination-aware value (market value,
		// falling back to tax basis) instead of recomputing owned x sharePrice here --
		// for dollar-denominated classes (income_allocations et al.) "owned" is a
		// capital-account balance, not a share count, so owned x sharePrice is meaningless
		// and was previously summing garbage into every non-share-denominated class.
		const currentValue = denominationAware
			? (calcClassData?.value ?? (denomination === 'shares' ? sharesOwned * currentPrice : sharesOwned))
			: sharesOwned * currentPrice;
		const ownedLabel = denominationAware ? resolveOwnedLabel(clientData, c) || (denomination === 'shares' ? 'Shares Owned' : 'Owned') : 'Current Value';

		// #5447: ownership % per class -- same fallback chain/fraction as the
		// dashboard headline's `ownershipDecimal` (HeadlineFormatter.js), so a
		// shareholder sees the same percentage here as on their own dashboard.
		const totalClassShares = denominationAware ? resolveClassTotalShares(c, { clientData, capTableData, client }) : null;
		const ownershipDecimal = !denominationAware
			? null
			: denomination === 'percent'
				? sharesOwned / 100
				: totalClassShares && sharesOwned
					? sharesOwned / totalClassShares
					: null;

		// #5447: tax basis per class -- only IncomeAllocationsCalculator's breakdown
		// carries this field (contributions + incomeAllocations - distributions +
		// transfers); other calculator methods leave it null, which hides the row.
		const taxBasis =
			denominationAware && typeof calcClassData?.taxBasis === 'number' && !Number.isNaN(calcClassData.taxBasis) ? calcClassData.taxBasis : null;

		// #5447: same "last entered data" source as the equity card's
		// currentClassValuationDate (index.js ~12745/13227) -- valuations[0] after
		// getEquityData()'s descending sort, not today's statement-generation date.
		const asOfDate = denominationAware && equityConfig.valuations?.[0]?.date ? moment.unix(equityConfig.valuations[0].date).format('MMM D, YYYY') : '';

		byClass[c] = {
			shareClass: c,
			shareClassName: equityConfig.name || c,
			denomination: denomination,
			ownedLabel: ownedLabel,
			sharesOwned: sharesOwned,
			currentPrice: currentPrice,
			currentValue: currentValue,
			ownershipDecimal: ownershipDecimal,
			taxBasis: taxBasis,
			asOfDate: asOfDate,
			transactions: [],
		};

		totalValue += currentValue;
	});

	// Process transactions into per-class arrays
	transactions.forEach((txn) => {
		const sc = txn.shareClass;
		if (!byClass[sc]) return;
		const qty = Number(String(txn.quantity || '').replace(/[^.\d]/g, '')) || 0;
		const price = Number(String(txn.sharePrice || '').replace(/[^.\d]/g, '')) || 0;
		byClass[sc].transactions.push({
			date: txn.date ? moment.unix(txn.date).format('MMM D, YYYY') : '',
			description: txn.notes || '',
			shares: qty,
			sharePrice: price,
			sender: txn.senderName || txn.sender || 'Issuer',
			receiver: txn.receiverName || txn.receiver || 'Issuer',
			certNumber: getCertNumber(txn.notes || txn.description || ''),
		});
	});

	// Get time series data for the chart (already built by TimeSeriesBuilder)
	const timeSeriesData = window.lastTimeSeriesResult?.data || [];

	return {
		statementDate,
		byClass,
		classes,
		totalValue,
		userName: targetUserData.user_name?.replace(/_/g, ' ') || '',
		timeSeriesData,
		transactionMode,
	};
}

export function buildStatementHtml(statementData, basisTableHtml, colors = {}) {
	/* Generates HTML body content for account statement (BW-style multi-page layout) */
	const primaryColor = colors.primaryColor || '#299aa4';
	const textColor = colors.textColor || '#243b53';
	const thStyle = `background-color: ${primaryColor}; color: white; padding: 10px 8px; text-align: center; font-weight: bold; font-size: 10px; text-transform: uppercase;`;
	// #5447: same decimals config + null-hides-the-row convention as the dashboard's ownershipPercentage formatting
	const ownershipDecimals = Number.isInteger(Number(clientData.decimalsConfig?.ownershipPercentage))
		? Number(clientData.decimalsConfig.ownershipPercentage)
		: 2;
	const formatOwnershipPercent = (decimal) => (decimal === null || decimal === undefined ? null : (decimal * 100).toFixed(ownershipDecimals) + '%');
	// #5447: tax basis can be legitimately negative (a class whose distributions/
	// transfers exceed contributions + income allocations) -- format the sign on
	// the outside of the $ prefix rather than "$-1,234".
	const formatSignedCurrency = (v) =>
		v < 0
			? '-$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
			: '$' + v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
	// #5447: shared "as of <date of last entered valuation>" sub-line for Price/Value/Tax Basis
	const asOfSuffix = (asOfDate) => (asOfDate ? `<br><span style="font-size: 9px; color: #64748b;">as of ${asOfDate}</span>` : '');
	// #5447: opt-in via clientData.flags.statement_denomination_aware (default false) --
	// gates the value/label/ownership%/tax-basis computation only. Ignored (always
	// true, prior behavior) for every client that hasn't turned it on.
	const denominationAware = clientData.flags?.statement_denomination_aware === true;
	// #5447: display toggles, independent of statement_denomination_aware -- any client
	// on this shared default statement path can turn these off, default true (shown).
	const showStatementTransactions = clientData.flags?.show_statement_transactions ?? true;
	// when off, the statement is just the page-1 summary, with no per-class detail pages following it.
	const showStatementClassPages = clientData.flags?.show_statement_class_pages ?? true;

	let html = `
	<!DOCTYPE html>
	<html>
	<head>
		<style>
			* { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; box-sizing: border-box; }
			html, body { height: auto !important; overflow: visible !important; }
			body {
				font-family: 'Source Sans Pro', Arial, sans-serif;
				font-size: 12px;
				margin: 0;
				padding: 0;
				color: ${textColor};
			}
			table { page-break-inside: auto; width: 100%; border-collapse: collapse; }
			thead { display: table-header-group; }
			tr { page-break-inside: avoid; page-break-after: auto; }
			.portfolio-summary {
				padding: 20px;
			}
			.portfolio-summary.page-break {
				page-break-after: always;
			}
			.class-section {
				padding: 20px;
			}
			.class-section.page-break {
				page-break-before: always;
			}
			.section-title {
				font-size: 16px;
				font-weight: bold;
				color: ${textColor};
				margin-bottom: 15px;
				border-bottom: 2px solid ${primaryColor};
				padding-bottom: 5px;
			}
			.holdings-grid {
				display: flex;
				flex-wrap: wrap;
				gap: 12px;
				margin-bottom: 20px;
			}
			.holding-card {
				background: white;
				border: 1px solid #e2e8f0;
				border-radius: 8px;
				padding: 12px 16px;
				min-width: 150px;
				flex: 1;
			}
			.holding-class {
				font-size: 11px;
				color: #666;
				text-transform: uppercase;
				margin-bottom: 4px;
			}
			.holding-shares {
				font-size: 16px;
				font-weight: bold;
				color: ${textColor};
			}
			.summary-grid {
				background: #f0f4f8;
				border-radius: 8px;
				padding: 15px;
				margin-bottom: 20px;
				display: flex;
				justify-content: space-between;
				flex-wrap: wrap;
			}
			.summary-item {
				text-align: center;
				flex: 1;
				min-width: 140px;
				padding: 10px;
			}
			.summary-label {
				font-size: 10px;
				color: #666;
				text-transform: uppercase;
				margin-bottom: 5px;
			}
			.summary-value {
				font-size: 16px;
				font-weight: bold;
				color: ${textColor};
			}
			.summary-value.large {
				font-size: 22px;
				color: ${primaryColor};
			}
			.summary-card {
				background: #f0f4f8;
				border-radius: 8px;
				padding: 15px;
				margin-bottom: 20px;
			}
			.summary-card h3 {
				margin: 0 0 12px 0;
				font-size: 14px;
				color: ${primaryColor};
				font-weight: bold;
			}
			.detail-table {
				width: 100%;
				border-collapse: collapse;
				margin-top: 15px;
				font-size: 11px;
			}
			.detail-table th {
				background-color: ${primaryColor};
				color: white;
				padding: 10px 8px;
				text-align: center;
				font-weight: bold;
				font-size: 10px;
				text-transform: uppercase;
			}
			.detail-table td {
				padding: 8px;
				border-bottom: 1px solid #e2e8f0;
			}
			.detail-table tr:nth-child(even) {
				background-color: #f8fafc;
			}
			.text-right { text-align: right; }
			.text-center { text-align: center; }
			.chart-section { margin-bottom: 20px; }
			.chart-title {
				font-size: 14px;
				font-weight: bold;
				color: ${textColor};
				margin-bottom: 10px;
			}
			.basis-section { margin-top: 30px; }
			.basis-section table { width: 100%; border-collapse: collapse; font-size: 11px; }
			.basis-section td { padding: 8px; border-bottom: 1px solid #e2e8f0; text-align: center; }
			.basis-section tr:nth-child(even) { background-color: #f8fafc; }
		</style>
	</head>
	<body>
	`;

	// === PAGE 1: Portfolio Summary ===
	// #5447: filter on currentValue, not sharesOwned -- a dollar-denominated LLC
	// class (e.g. LIGON) can carry a legitimate negative capital-account balance,
	// which `sharesOwned > 0` silently dropped entirely. Only takes effect once
	// statement_denomination_aware is on; every other client keeps the original
	// sharesOwned > 0 predicate.
	const classesWithShares = statementData.classes.filter((c) => {
		const cd = statementData.byClass[c];
		const hasHolding = denominationAware ? cd.currentValue !== 0 : cd.sharesOwned > 0;
		return hasHolding || (cd.transactions && cd.transactions.length > 0);
	});

	// #5447: page-break only when per-class pages will actually follow (either because
	// show_statement_class_pages is off, or because there's simply nothing to show) --
	// avoids a trailing forced break on an otherwise single-page statement.
	const willRenderClassPages = showStatementClassPages && classesWithShares.length > 0;
	html += `<div class="portfolio-summary${willRenderClassPages ? ' page-break' : ''}">`;

	// Holdings Cards
	html += `
		<div class="section-title">Your Holdings</div>
		<div class="holdings-grid">
	`;
	classesWithShares.forEach((c) => {
		const classData = statementData.byClass[c];
		// #5447: only share-denominated classes have a meaningful share count --
		// everything else (income_allocations et al.) shows its computed value instead.
		const holdingLine =
			classData.denomination === 'shares'
				? `${classData.sharesOwned.toLocaleString('en-US')} shares`
				: `$${classData.currentValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
		html += `
			<div class="holding-card">
				<div class="holding-class">${escapeStatementHtml(classData.shareClassName)}</div>
				<div class="holding-shares">${holdingLine}</div>
			</div>
		`;
	});
	html += `</div>`;

	// Summary Grid
	html += `
		<div class="summary-grid">
			<div class="summary-item">
				<div class="summary-label">Statement Date</div>
				<div class="summary-value">${statementData.statementDate}</div>
			</div>
			<div class="summary-item">
				<div class="summary-label">Total Current Value</div>
				<div class="summary-value large">$${statementData.totalValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
			</div>
		</div>
	`;

	// Portfolio Value Chart
	// #5447: independent of statement_denomination_aware -- any client on this shared
	// default statement path can turn this off, default true (shown). The chart's
	// axis/summing assumes a single portfolio-wide dollar total, which doesn't hold
	// for every calculator method, so an admin may want it off regardless of denomination.
	const showStatementChart = clientData.flags?.show_statement_chart ?? true;
	if (showStatementChart && statementData.timeSeriesData && statementData.timeSeriesData.length >= 2) {
		const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
		const chartData = statementData.timeSeriesData.filter((row) => row[0] < oneDayAgo);
		if (chartData.length >= 2) {
			const chartSvg = buildStatementChartSvg(chartData, { width: 700, height: 180, color: primaryColor });
			if (chartSvg) {
				html += `
					<div class="chart-section">
						<div class="chart-title">Portfolio Value Over Time</div>
						<div style="padding: 10px 0;">${chartSvg}</div>
					</div>
				`;
			}
		}
	}

	// Value by Class breakdown on summary page
	if (classesWithShares.length > 0 && denominationAware) {
		// #5447: an explicit column-headed table -- clearly labels each figure instead
		// of the original unlabeled inline "shares × price = value" string (kept
		// verbatim below for every client with statement_denomination_aware off).
		// classes agree on one label per resolveOwnedLabel's client-wide fallback (llproducts:
		// "Value") unless a class-level override exists; use that as one shared column header.
		const valueColumnLabel = escapeStatementHtml(resolveOwnedLabel(clientData, null) || 'Current Value');
		html += `
			<div class="section-title">Value by Share Class</div>
			<table class="detail-table">
				<thead>
					<tr>
						<th bgcolor="${primaryColor}" style="${thStyle}">Share Class</th>
						<th bgcolor="${primaryColor}" style="${thStyle}">Ownership %</th>
						<th bgcolor="${primaryColor}" style="${thStyle}">Shares</th>
						<th bgcolor="${primaryColor}" style="${thStyle}">Price</th>
						<th bgcolor="${primaryColor}" style="${thStyle}">${valueColumnLabel}</th>
						<th bgcolor="${primaryColor}" style="${thStyle}">Tax Basis</th>
					</tr>
				</thead>
				<tbody>
		`;
		classesWithShares.forEach((c) => {
			const cd = statementData.byClass[c];
			// shares/price only apply to share-denominated classes; a
			// capital-account class has no per-share price -- em-dash, not $0/0.
			const sharesText = cd.denomination === 'shares' ? cd.sharesOwned.toLocaleString('en-US') : '—';
			const priceText =
				cd.denomination === 'shares'
					? '$' + cd.currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + asOfSuffix(cd.asOfDate)
					: '—';
			const ownershipPercentText = formatOwnershipPercent(cd.ownershipDecimal) || '—';
			const valueText =
				`<strong>$${cd.currentValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</strong>` +
				asOfSuffix(cd.asOfDate);
			const taxBasisText = cd.taxBasis !== null ? formatSignedCurrency(cd.taxBasis) + asOfSuffix(cd.asOfDate) : '—';
			html += `
				<tr>
					<td>${escapeStatementHtml(cd.shareClassName)}</td>
					<td class="text-center">${ownershipPercentText}</td>
					<td class="text-right">${sharesText}</td>
					<td class="text-right">${priceText}</td>
					<td class="text-right">${valueText}</td>
					<td class="text-right">${taxBasisText}</td>
				</tr>
			`;
		});
		html += `
				</tbody>
			</table>
		`;
	} else if (classesWithShares.length > 0) {
		// Original markup, unchanged, for every other client on this shared path.
		html += `<div class="section-title">Value by Share Class</div>`;
		classesWithShares.forEach((c) => {
			const cd = statementData.byClass[c];
			html += `
				<div style="display: flex; justify-content: space-between; padding: 10px 15px; border-bottom: 1px solid #e2e8f0;">
					<div style="font-weight: bold;">${escapeStatementHtml(cd.shareClassName)}</div>
					<div style="text-align: right;">
						${cd.sharesOwned.toLocaleString('en-US')} shares × $${cd.currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} =
						<strong>$${cd.currentValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</strong>
					</div>
				</div>
			`;
		});
	}

	html += `</div>`; // close .portfolio-summary

	// === SUBSEQUENT PAGES: Per-Class Detail with Transactions ===
	let isFirstClass = true;
	(showStatementClassPages ? classesWithShares : []).forEach((c) => {
		const classData = statementData.byClass[c];
		const pageBreakClass = isFirstClass ? '' : 'page-break';
		isFirstClass = false;

		// #5447: share count / per-share price only apply to share-denominated
		// classes -- a capital-account class only ever shows its resolved value,
		// labeled per clientData.language.label_owned when the client configures one.
		const shareDetailFields =
			classData.denomination === 'shares'
				? `
				<div class="summary-item">
					<div class="summary-label">Shares Owned</div>
					<div class="summary-value">${classData.sharesOwned.toLocaleString('en-US')}</div>
				</div>
				<div class="summary-item">
					<div class="summary-label">Current Price${classData.asOfDate ? ` as of ${classData.asOfDate}` : ''}</div>
					<div class="summary-value">$${classData.currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
				</div>
			`
				: '';
		// #5447: hidden (per formatOwnershipPercent) when no total-shares-outstanding
		// figure resolves for this class -- see resolveClassTotalShares's fallback chain.
		const ownershipPercentText = formatOwnershipPercent(classData.ownershipDecimal);
		const ownershipField = ownershipPercentText
			? `
				<div class="summary-item">
					<div class="summary-label">Ownership %</div>
					<div class="summary-value">${ownershipPercentText}</div>
				</div>
			`
			: '';
		// #5447: null when this class's calculator method doesn't produce a tax
		// basis figure (only IncomeAllocationsCalculator does today) -- hides the row.
		// Dated by this class's own last-entered valuation (classData.asOfDate),
		// same source as the equity card's currentClassValuationDate -- not the
		// statement's generation date, which is a different concept (statementDate).
		const taxBasisField =
			classData.taxBasis !== null
				? `
				<div class="summary-item">
					<div class="summary-label">Tax Basis${classData.asOfDate ? ` as of ${classData.asOfDate}` : ''}</div>
					<div class="summary-value">${formatSignedCurrency(classData.taxBasis)}</div>
				</div>
			`
				: '';
		html += `
		<div class="class-section ${pageBreakClass}">
			<div class="summary-card">
				<h3>${escapeStatementHtml(classData.shareClassName)}</h3>
				<div style="display: flex; justify-content: space-between; flex-wrap: wrap;">
					${shareDetailFields}
					${ownershipField}
					<div class="summary-item">
						<div class="summary-label">${escapeStatementHtml(classData.ownedLabel)}${classData.asOfDate ? ` as of ${escapeStatementHtml(classData.asOfDate)}` : ''}</div>
						<div class="summary-value">$${classData.currentValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
					</div>
					${taxBasisField}
				</div>
			</div>
		`;

		// Transaction table for this class
		if (showStatementTransactions && classData.transactions && classData.transactions.length > 0) {
			const hasPrice = classData.transactions.some((txn) => txn.sharePrice > 0);
			const isGiftTransfer = statementData.transactionMode === 'gift_transfer';
			html += `
			<table class="detail-table">
				<thead>
					<tr>
						<th bgcolor="${primaryColor}" style="${thStyle}">Date</th>
						${isGiftTransfer ? `<th bgcolor="${primaryColor}" style="${thStyle}">Cert #</th>` : ''}
						<th bgcolor="${primaryColor}" style="${thStyle}">Description</th>
						${!isGiftTransfer ? `<th bgcolor="${primaryColor}" style="${thStyle}">Sender</th>` : ''}
						${!isGiftTransfer ? `<th bgcolor="${primaryColor}" style="${thStyle}">Receiver</th>` : ''}
						<th bgcolor="${primaryColor}" style="${thStyle}">Quantity</th>
						${hasPrice ? `<th bgcolor="${primaryColor}" style="${thStyle}">Share Price</th>` : ''}
					</tr>
				</thead>
				<tbody>
			`;

			classData.transactions.forEach((txn) => {
				html += `
					<tr>
						<td class="text-center">${escapeStatementHtml(txn.date)}</td>
						${isGiftTransfer ? `<td class="text-center">${escapeStatementHtml(txn.certNumber || '')}</td>` : ''}
						<td>${escapeStatementHtml(txn.notes || txn.description)}</td>
						${!isGiftTransfer ? `<td>${escapeStatementHtml(txn.sender)}</td>` : ''}
						${!isGiftTransfer ? `<td>${escapeStatementHtml(txn.receiver)}</td>` : ''}
						<td class="text-right">${txn.shares.toLocaleString('en-US')}</td>
						${hasPrice ? `<td class="text-right">${txn.sharePrice ? '$' + txn.sharePrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}</td>` : ''}
					</tr>
				`;
			});

			html += `
				</tbody>
			</table>
			`;
		}

		html += `</div>`; // close .class-section
	});

	// Basis Table Section (if enabled)
	if (basisTableHtml) {
		html += `
			<div class="class-section page-break">
				<div class="basis-section">
					<div class="section-title">Cost Basis Details</div>
					${basisTableHtml}
				</div>
			</div>
		`;
	}

	html += `
	</body>
	</html>
	`;

	return html;
}

export function buildStatementHeaderTemplate(targetUserData, logoBase64 = '', colors = {}) {
	/* Generates PDF header template for account statement */
	const primaryColor = colors.primaryColor || '#299aa4';
	const textColor = colors.textColor || '#243b53';

	const userName = escapeStatementHtml(targetUserData.user_name?.replace(/_/g, ' ') || '');
	const companyName = escapeStatementHtml(clientData.company_name || '');

	const logoOrName = logoBase64 ? `<img class="company-logo" src="${logoBase64}" alt="${companyName}">` : `<span class="company-name">${companyName}</span>`;

	return `<style>
		* { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
		.header-container { width: 100%; font-family: Arial, sans-serif; padding: 15px 30px; font-size: 10px; }
		.company-logo { max-height: 50px; }
		.company-name { font-size: 20px; font-weight: bold; color: ${primaryColor}; }
		.header-row { display: flex; justify-content: space-between; align-items: center; margin-top: 10px; }
		.statement-title { font-size: 14px; font-weight: bold; color: ${textColor}; }
		.user-name { font-size: 12px; color: #666; }
	</style>
	<div class="header-container">
		<div style="text-align: center;">
			${logoOrName}
		</div>
		<div class="header-row">
			<div class="statement-title">Shareholder Account Statement</div>
			<div class="user-name">${userName}</div>
		</div>
	</div>`;
}

export function buildStatementFooterTemplate() {
	/* Generates PDF footer template for account statement */
	return `<style>
		* { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
		.footer-container { width: 100%; font-family: Arial, sans-serif; font-size: 9px; padding: 8px 30px; color: #666; }
		.footer-inner { border-top: 1px solid #e2e8f0; padding: 8px 0; display: flex; justify-content: space-between; align-items: center; }
		.page-num { font-size: 10px; color: #243b53; }
	</style>
	<div class="footer-container">
		<div class="footer-inner">
			<div>Confidential - For recipient only</div>
			<div class="page-num">Page <span class="pageNumber"></span></div>
		</div>
	</div>`;
}
